import styled from 'styled-components'
import { ChevronDownIcon, ChevronUpIcon } from '@shipt/design-system-icons'
import { useActiveFiltersLengths } from '@/features/shop/services/Search/hooks'
import { useFiltersContext } from '@/features/shop/components/ProductFilter/FiltersContext'
import { FilterPill } from '@/features/shop/components/ProductFilter/FilterOptions/FilterPill'
import { useOnFilterPillClick } from '@/features/shop/components/ProductFilter/interactions/useOnFilterPillClick'
import { FilterPopover } from '@/features/shop/components/ProductFilter/FilterPopover'
import { useDetectFocusOutside } from '@/features/shared/hooks/useDetectFocusOutside'
import { ResetFilterButton } from '@/features/shop/components/ProductFilter/ResetFilterButton'
import { type SEARCH_FILTER_TYPE } from '@/features/shared/constants/global'
import { type JSX } from 'react'
import { Row } from '@shipt/design-system-layouts'

type Props = {
  className?: string
  renderImg?: () => JSX.Element
  onClick?: () => void
  name: string
  filterType: SEARCH_FILTER_TYPE
  children: React.ReactNode
}

export const FilterPillContent = ({
  className,
  renderImg,
  onClick,
  name,
  filterType,
  children,
}: Props) => {
  const { ref, getRef, willOverflow, isPopoverOpen, togglePopover, onFilterPillClick } =
    useOnFilterPillClick(filterType)
  const { facets } = useFiltersContext()
  const activeFiltersLengths = useActiveFiltersLengths(facets)

  const activeFiltersLength = activeFiltersLengths[filterType]
  const isFilterActive = Boolean(activeFiltersLength)
  const isPillActive = isFilterActive || isPopoverOpen

  useDetectFocusOutside([ref], togglePopover, isPopoverOpen)
  const handleOnFilterPillClick = () => {
    onClick?.()
    onFilterPillClick()
  }

  return (
    <FilterPillContentWrapper className={className} ref={getRef}>
      <Container>
        <FilterContentPill
          onClick={handleOnFilterPillClick}
          isActive={isPillActive}
          isFilterActive={isFilterActive}
        >
          {renderImg?.()}
          <FilterName>{name}</FilterName>
          {activeFiltersLength > 1 && <span>+{activeFiltersLength - 1}</span>}
          {!isFilterActive && (
            <ChevronIconsWrapper>
              {isPopoverOpen ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
            </ChevronIconsWrapper>
          )}
        </FilterContentPill>
        {!!isFilterActive && <ResetFilterButton filterType={filterType} />}
      </Container>
      {!!isPopoverOpen && (
        <FilterPopover togglePopover={togglePopover} willOverflow={willOverflow}>
          {children}
        </FilterPopover>
      )}
    </FilterPillContentWrapper>
  )
}

const FilterPillContentWrapper = styled.div`
  display: inline-block;
`

const Container = styled(Row)`
  /* prevent filter popover from being hidden by layout */
  position: relative;
`

const FilterContentPill = styled(FilterPill)<{ isFilterActive: boolean }>`
  && {
    max-width: 15rem;
    padding-right: ${({ isFilterActive }) => (isFilterActive ? 36 : 0)}px;
  }

  & > .buttonContent {
    max-width: 100%;
  }
`

const FilterName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ChevronIconsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  margin-left: 0.25rem;
`
